/**
 * Детализация модификаторов из MobileOrder (бэк уже посчитал freeQuantity).
 *
 * - `free_amount` — бесплатных единиц на одну порцию блюда (0 — нет)
 * - `total_new_price` — стоимость мода на одну порцию с учётом бесплатных и скидок
 *
 * Сумма в строке = total_new_price × кол-во блюда + delta_price
 * Платных единиц = amount − free_amount
 * Цена платной единицы = total_new_price / кол-во платных единиц
 */

export interface OrderModifierInput {
	amount?: number | null;
	free_amount?: number | null;
	price?: number | null;
	total_new_price?: number | null;
	delta_price?: number | string | null;
}

const toNonNegativeInt = (value: number | null | undefined, fallback = 0): number => {
	const amount = Math.floor(Number(value));
	if (!Number.isFinite(amount) || amount < 0) return fallback;
	return amount;
};

const toPrice = (value: number | string | null | undefined): number => {
	const price = Number(value);
	return Number.isFinite(price) ? price : 0;
};

/** Бесплатных единиц на одну порцию блюда (не больше amount — защита от битых данных бэка). */
export const getOrderModifierFreeAmount = (modifier: OrderModifierInput | null | undefined): number => {
	const amount = toNonNegativeInt(modifier?.amount, 0);
	const freeAmount = toNonNegativeInt(modifier?.free_amount, 0);
	return Math.min(amount, freeAmount);
};

/** Платных единиц на одну порцию: amount − free_amount. */
export const getOrderModifierPaidAmount = (modifier: OrderModifierInput | null | undefined): number => {
	const amount = toNonNegativeInt(modifier?.amount, 0);
	return Math.max(0, amount - getOrderModifierFreeAmount(modifier));
};

/** Стоимость мода на одну порцию (уже с бесплатными и скидками). */
export const getOrderModifierPerPortionTotal = (modifier: OrderModifierInput | null | undefined): number => {
	if (!modifier) return 0;
	if (modifier.total_new_price != null) return toPrice(modifier.total_new_price);
	return toPrice(modifier.price) * toNonNegativeInt(modifier.amount, 1);
};

/** Цена одной платной единицы: total_new_price / paidAmount. */
export const getOrderModifierPaidUnitPrice = (modifier: OrderModifierInput | null | undefined): number => {
	const paidAmount = getOrderModifierPaidAmount(modifier);
	if (paidAmount <= 0) return 0;
	return getOrderModifierPerPortionTotal(modifier) / paidAmount;
};

/** Итог строки мода: total_new_price × dishAmount + delta_price. */
export const getOrderModifierLineTotal = (modifier: OrderModifierInput | null | undefined, dishAmount = 1): number => {
	const portions = toNonNegativeInt(dishAmount, 1) || 1;
	return getOrderModifierPerPortionTotal(modifier) * portions + toPrice(modifier?.delta_price);
};

export const shouldShowOrderModifierFormula = (modifier: OrderModifierInput | null | undefined, dishAmount = 1): boolean => {
	const amount = toNonNegativeInt(modifier?.amount, 0);
	const freeAmount = getOrderModifierFreeAmount(modifier);
	const portions = toNonNegativeInt(dishAmount, 1);
	return amount > 1 || portions > 1 || freeAmount > 0;
};
